import { useState, useEffect } from "react";
import axios from "axios";
import { ArrowLeft, Calendar, Headphones, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPage({ onListen }) {
  const [posts, setPosts] = useState([]);
  const [post, setPost] = useState(null);

  useEffect(() => {
    axios.get(`${API}/blog/posts`).then((r) => setPosts(r.data)).catch(() => {});
  }, []);

  if (post) {
    return (
      <div className="mx-auto max-w-lg pb-8" data-testid="blog-post-detail">
        <div className="relative h-56 w-full overflow-hidden">
          <img src={post.image} alt={post.title} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#07090E] via-[#07090E]/30 to-transparent" />
          <button
            data-testid="blog-back-btn"
            onClick={() => setPost(null)}
            className="absolute left-4 top-4 flex items-center gap-1.5 rounded-full border border-white/15 bg-black/40 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-md"
          >
            <ArrowLeft size={14} /> Blog
          </button>
        </div>
        <div className="px-4">
          <p className="mt-4 flex items-center gap-2 text-[11px] text-slate-400">
            <span className="rounded-full bg-amber-300/10 px-2 py-0.5 font-medium text-amber-200">{post.category}</span>
            <span className="flex items-center gap-1"><Calendar size={12} /> {post.date}</span>
          </p>
          <h1 className="mt-2 text-2xl font-semibold leading-tight text-white" style={{ fontFamily: "'Syne', sans-serif" }}>
            {post.title}
          </h1>
          <button
            data-testid={`blog-listen-detail-${post.slug}`}
            onClick={() => onListen && onListen(post.slug)}
            className="mt-4 flex items-center gap-2 rounded-full border border-amber-300/30 bg-amber-300/10 px-4 py-2 text-[13px] font-semibold text-amber-200 transition-colors hover:bg-amber-300/20"
          >
            <Headphones size={15} /> Listen to this episode
          </button>
          <div className="mt-4 space-y-3">
            {post.body.map((p, i) => (
              <p key={i} className="text-[14px] leading-relaxed text-slate-300">{p}</p>
            ))}
          </div>
          <a
            data-testid={`blog-readfull-${post.slug}`}
            href={post.url}
            target="_blank"
            rel="noreferrer"
            className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-amber-200 hover:underline"
          >
            Read the full article on Gateway2World <ExternalLink size={14} />
          </a>
          <p className="mt-6 rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 text-[11px] italic text-slate-400">
            Summary of a Gateway2World article — general information only, not regulated migration, legal or tax advice.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg px-4 pb-8 pt-4" data-testid="blog-page">
      <h1 className="text-2xl font-semibold text-white" style={{ fontFamily: "'Syne', sans-serif" }}>Insights & Blog</h1>
      <p className="mt-1 text-sm text-slate-300">Guides and perspectives on global residency, citizenship and relocation.</p>

      <div className="mt-5 space-y-4">
        {posts.map((p) => (
          <div
            key={p.slug}
            className="group block w-full overflow-hidden rounded-2xl border border-white/[0.07] bg-white/[0.03] text-left transition-colors hover:border-amber-300/30"
          >
            <button
              data-testid={`blog-card-${p.slug}`}
              onClick={() => setPost(p)}
              className="block w-full text-left"
            >
              <div className="h-40 w-full overflow-hidden">
                <img src={p.image} alt={p.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="px-4 pt-4">
                <p className="flex items-center gap-2 text-[11px] text-slate-400">
                  <span className="rounded-full bg-amber-300/10 px-2 py-0.5 font-medium text-amber-200">{p.category}</span>
                  <span className="flex items-center gap-1"><Calendar size={12} /> {p.date}</span>
                </p>
                <h2 className="mt-1.5 text-base font-semibold leading-snug text-white">{p.title}</h2>
                <p className="mt-1.5 text-[13px] leading-snug text-slate-300">{p.excerpt}</p>
              </div>
            </button>
            <div className="flex items-center justify-between px-4 pb-4 pt-2">
              <button
                data-testid={`blog-read-${p.slug}`}
                onClick={() => setPost(p)}
                className="text-xs font-semibold text-amber-200"
              >
                Read more →
              </button>
              <button
                data-testid={`blog-listen-${p.slug}`}
                onClick={() => onListen && onListen(p.slug)}
                className="flex items-center gap-1.5 rounded-full border border-amber-300/30 bg-amber-300/10 px-3 py-1.5 text-[11px] font-semibold text-amber-200 transition-colors hover:bg-amber-300/20"
              >
                <Headphones size={13} /> Listen
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
